import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Tabs,
  Tab,
  Divider,
  Chip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { AccessTime as TimeIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SpecialRequestForm from './SpecialRequestForm';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../../hooks/useLocalizedField';
import { categoriesApi } from '../../firebase/services';
import { Category } from '../../types';

interface Service {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  duration: number;
  price: { min: number; max: number };
  category: string;
  popular: boolean;
  showPrice?: boolean;
  translations?: {
    fr?: { name?: string; description?: string };
    es?: { name?: string; description?: string };
  };
}

const ServicesContent: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const theme = useTheme();
  const { t } = useTranslation();
  const { getField } = useLocalizedField();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [querySnapshot, cats] = await Promise.all([
          getDocs(collection(db, 'services')),
          categoriesApi.getAll(),
        ]);

        const servicesData: Service[] = [];
        querySnapshot.forEach((doc) => {
          servicesData.push({ id: doc.id, ...doc.data() } as Service);
        });

        setServices(servicesData);
        setCategories(cats);
      } catch (error) {
        console.error('Error fetching services/categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const filteredServices = React.useMemo(() => {
    return activeCategory === 'All'
      ? services
      : services.filter(service => service.category === activeCategory);
  }, [services, activeCategory]);

  const popularServices = React.useMemo(() => {
    return services.filter(service => service.popular);
  }, [services]);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (services.length === 0) {
    return (
      <Box sx={{ py: 8 }}>
        <Container>
          <Typography variant="h4" align="center" color="text.secondary">
            {t('services.noServices')}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Typography variant="h2" component="h1" align="center" gutterBottom>
          {t('services.title')}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
        >
          {t('services.subtitle')}
        </Typography>

        {/* Popular Services Section */}
        {popularServices.length > 0 && (
          <Box sx={{ mb: 10 }}>
            <Typography
              variant="h4"
              component="h2"
              align="center"
              sx={{ mb: 4, fontWeight: 600 }}
            >
              {t('services.popular')}
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: 4,
              mb: 4
            }}>
              {popularServices.map((service) => (
                <Box key={`popular-${service.id}`}>
                  <Card
                    onClick={() => navigate('/booking', { state: { service } })}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      minHeight: 350,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height={200}
                      image={service.imageUrl}
                      alt={service.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {getField(service, 'name', service.name)}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1
                        }}
                      >
                        {service.showPrice !== false && (
                          <Typography variant="body1" fontWeight={500}>
                            ${service.price.min} - ${service.price.max}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {service.duration} {t('common.min')}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {getField(service, 'description', service.description)}
                      </Typography>
                      <Chip
                        label={getField(
                          categories.find(c => c.name === service.category) || { id: '', name: service.category },
                          'name',
                          service.category
                        )}
                        size="small"
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'text.primary',
                          alignSelf: 'flex-start'
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 8 }} />

        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{ mb: 4, fontWeight: 600 }}
        >
          {t('services.all')}
        </Typography>

        <Tabs
          value={activeCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 6,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              minWidth: 100,
            },
            '& .Mui-selected': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
        >
          <Tab
            key="All"
            label={t('services.categories.All', { defaultValue: 'Tous' })}
            value="All"
          />
          {categories.map((cat) => (
            <Tab
              key={cat.id}
              label={getField(cat, 'name', cat.name)}
              value={cat.name}
            />
          ))}
        </Tabs>

        {filteredServices.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)'
            },
            gap: 4,
            alignItems: 'start'
          }}>
            {filteredServices.map((service) => (
              <Box key={`filtered-${service.id}`}>
                <Card
                  onClick={() => navigate('/booking', { state: { service } })}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    minHeight: 200,
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      height: { xs: 180, sm: 180 },
                      objectFit: 'cover',
                      flexShrink: 0
                    }}
                    image={service.imageUrl}
                    alt={service.name}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {getField(service, 'name', service.name)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                      {getField(service, 'description', service.description)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 'auto'
                      }}
                    >
                      {service.showPrice !== false && (
                        <Typography variant="body1" fontWeight={500}>
                          ${service.price.min} - ${service.price.max}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {service.duration} {t('common.min')}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('services.noServicesInCategory')}
            </Typography>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="h5" component="p" gutterBottom>
            {t('services.ctaTitle')}
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2, color: 'text.primary' }}
          >
            {t('services.bookNow')}
          </Button>
        </Box>
      </Container>

      {/* Special request section */}
      <SpecialRequestForm />
    </Box>
  );
};

export default ServicesContent;
